// ============================================================
// KICKOFF BUILDER CONFIG
// Services, questions, recommendation rules, and sort order
// for the LMR Kickoff Builder wizard.
// ============================================================

export const SERVICES = [
  { id: 'website', label: 'Website / Landing Pages' },
  { id: 'seo', label: 'SEO' },
  { id: 'social', label: 'Organic Social' },
  { id: 'ads', label: 'Paid Ads' },
  { id: 'email', label: 'Email / Newsletters' },
  { id: 'design', label: 'Branding / Design' },
]

export const AD_PLATFORMS = [
  { id: 'google_ads', label: 'Google Ads' },
  { id: 'fb_ads', label: 'Facebook / Meta Ads' },
]

// Maps the template service_tags array values → service_area values in tasks
export const SERVICE_TAG_TO_AREA = {
  Website: 'website',
  SEO: 'seo',
  Ads: 'ads',
  Social: 'social',
  Email: 'email',
}

// Maps default_week string → week_number integer
export const WEEK_TO_NUMBER = {
  W1: 1, 'W1-W2': 1, W2: 2, 'W2-W3': 2, W3: 3,
}

// Execution order — lower number = earlier in the flow
// Templates without an entry sort to the bottom alphabetically
export const SORT_ORDER = {
  // Universal
  'checklist-onboarding': 10,
  'std-request-domain-access': 11,
  'std-request-backend-access': 12,
  'std-setup-calendly': 13,
  'std-setup-lmr-data-dashboard': 14,

  // Website
  'std-send-website-questionnaire': 100,
  'std-audit-website-ux': 101,
  'std-audit-website-branding': 102,
  'std-audit-website-structure': 103,
  'std-compile-website-changes': 104,
  'std-create-wpengine': 105,
  'std-create-mood-boards': 105.5,
  'std-build-aesthetic-1': 106,
  'std-build-aesthetic-2': 107,
  'std-build-aesthetic-3': 108,
  'std-build-aesthetic-key-pages': 109,
  'std-website-build-checklist': 110,
  'std-delegate-remaining-pages': 110,
  'std-logo-concepts': 111,
  'std-brand-kit-presentation': 112,
  'checklist-website-lps': 113,

  // SEO  (audit → GSC → list pages → keyword research → yoast → blog topics → assign blogs)
  'std-audit-rankings': 200,
  'std-verify-gsc-access': 201,
  'std-baseline-rankings-gsc': 202,
  'std-internal-seo-checklist': 203,
  'std-list-pages-keywords': 204,
  'std-keyword-research-pages': 205,
  'std-assign-yoast-aam': 206,
  'std-brainstorm-blog-topics': 207,
  'std-blog-topics-approved': 208,
  'std-assign-first-blogs': 209,

  // Ads  (audits → access → checklists → keyword research → ad groups → LPs → budget → review → launch)
  'std-audit-google-ads': 300,
  'std-audit-fb-ads': 301,
  'std-audit-existing-lps': 302,
  'std-send-google-ads-cid-request': 303,
  'std-request-google-ads-from-client': 304,
  'std-request-fb-business-access': 305,
  'checklist-google-ads': 306,
  'checklist-fb-ads': 307,
  'std-keyword-research-ad-groups': 308,
  'std-determine-ad-groups': 309,
  'std-build-first-lp': 310,
  'std-build-remaining-lps': 311,
  'std-budget-forecast': 312,
  'std-review-budget-with-client': 313,

  // Social
  'checklist-organic-social': 400,

  // Email
  'std-audit-newsletter-setup': 500,
  'std-confirm-newsletter-platform': 501,
  'std-newsletter-header-footer': 502,
  'checklist-newsletter': 503,

  // Hubspot (cross-cuts ads + email)
  'checklist-hubspot': 600,
}

// Service area for grouping in Review step — uses SERVICE_AREAS values from constants.js
export const REVIEW_SECTIONS = [
  { key: null, label: 'Universal' },              // tasks with no service_area
  { key: 'website', label: 'Website' },
  { key: 'seo', label: 'SEO' },
  { key: 'ads', label: 'Ads' },
  { key: 'social', label: 'Social' },
  { key: 'email', label: 'Email / Newsletters' },
]

// ────────────────────────────────────────────────────────────
// QUESTIONS
// ────────────────────────────────────────────────────────────

export const QUESTIONS = {
  universal: [
    { id: 'has_existing_website', text: 'Does the client have an existing website?', type: 'single', options: ['Yes', 'No'] },
    { id: 'website_status', text: 'Are we building a new website or working on the existing one?', type: 'single', options: ['New website', 'Existing website', 'Landing pages only', 'Not sure yet'], showIf: a => a.services?.includes('website') },
    { id: 'uses_hubspot', text: 'Will we use HubSpot?', type: 'single', options: ['Yes', 'No', 'Not sure yet'], showIf: a => a.services?.includes('ads') || a.services?.includes('email') },
    { id: 'wp_engine_access', text: 'WP Engine access?', type: 'single', options: ['Client has it', 'We need to create it', 'Not applicable'], showIf: a => a.services?.includes('website') || a.services?.includes('seo') },
  ],

  website: [
    { id: 'website_build_aesthetics', text: 'Do we need to present aesthetic options?', type: 'single', options: ['Yes — 3 options', 'No — already have direction'], showIf: a => a.website_status === 'New website' },
    { id: 'needs_logo', text: 'Does the client need a new logo?', type: 'single', options: ['Yes', 'No'] },
    { id: 'needs_brand_kit', text: 'Does the client need a brand kit?', type: 'single', options: ['Yes', 'No'] },
    { id: 'audit_existing_website', text: 'Should we audit the existing website?', type: 'multi', options: ['UX', 'Branding', 'Site structure'], showIf: a => a.has_existing_website === 'Yes' },
  ],

  seo: [
    { id: 'has_gsc_access', text: 'Do we already have Google Search Console access?', type: 'single', options: ['Yes', 'No', 'Need to set up GSC'] },
    { id: 'wants_blogs', text: 'Will we be writing blogs?', type: 'single', options: ['Yes', 'No'] },
  ],

  social: [
    // Social runs almost entirely through the organic-social checklist.
    // No additional questions needed.
  ],

  ads: [
    { id: 'has_existing_google_ads', text: 'Does the client have an existing Google Ads account?', type: 'single', options: ['Yes — we should audit it', 'Yes — but no audit needed', 'No'], showIf: a => a.ad_platforms?.includes('google_ads') },
    { id: 'has_existing_fb_ads', text: 'Does the client have an existing Facebook Ads account?', type: 'single', options: ['Yes — we should audit it', 'Yes — but no audit needed', 'No'], showIf: a => a.ad_platforms?.includes('fb_ads') },
    { id: 'google_ads_access', text: 'How will we get Google Ads access?', type: 'single', options: ['Send CID request', 'Request from client', 'Already have it'], showIf: a => a.ad_platforms?.includes('google_ads') },
    { id: 'fb_business_access', text: 'How will we get FB business account access?', type: 'single', options: ['Request from client', 'Already have it'], showIf: a => a.ad_platforms?.includes('fb_ads') },
    { id: 'needs_lps', text: 'Will we be building landing pages for ads?', type: 'single', options: ['Yes — multiple', 'Yes — just one to start', 'No — using existing pages'] },
  ],

  email: [
    { id: 'has_existing_newsletter', text: 'Does the client have an existing newsletter?', type: 'single', options: ['Yes', 'No'] },
    { id: 'newsletter_platform', text: 'Which newsletter platform?', type: 'single', options: ['HubSpot', 'Constant Contact', 'Other', 'Not sure yet'] },
  ],
}

// ────────────────────────────────────────────────────────────
// RECOMMENDATION RULES
// Each rule: when(answers) returns true → recommend template_id
// ────────────────────────────────────────────────────────────

export const RECOMMENDATION_RULES = [
  // ── UNIVERSAL ──────────────────────────────────────────────
  { template_id: 'checklist-onboarding', reason: 'Standard onboarding for every new client', when: () => true },
  { template_id: 'std-request-domain-access', reason: 'Website, SEO, or Ads services need domain access', when: a => a.services?.some(s => ['website', 'seo', 'ads'].includes(s)) },
  { template_id: 'std-request-backend-access', reason: 'Website or SEO work needs backend access', when: a => a.services?.some(s => ['website', 'seo'].includes(s)) },
  { template_id: 'std-setup-calendly', reason: 'Calendly setup for booking/intake', when: a => a.services?.includes('ads') || a.uses_hubspot === 'Yes' },
  { template_id: 'std-setup-lmr-data-dashboard', reason: 'Every client gets an LMR Data Dashboard (week 3)', when: () => true },

  // ── WEBSITE ───────────────────────────────────────────────
  { template_id: 'checklist-website-lps', reason: 'Website work selected', when: a => a.services?.includes('website') },
  { template_id: 'std-website-build-checklist', reason: 'Full website build (new site or rebuild of existing)', when: a => a.services?.includes('website') && (a.website_status === 'New website' || a.website_status === 'Existing website') },
  { template_id: 'std-send-website-questionnaire', reason: 'New website needs discovery questionnaire', when: a => a.website_status === 'New website' },
  { template_id: 'std-audit-website-ux', reason: 'Existing website UX audit requested', when: a => a.audit_existing_website?.includes('UX') },
  { template_id: 'std-audit-website-branding', reason: 'Existing website branding audit requested', when: a => a.audit_existing_website?.includes('Branding') },
  { template_id: 'std-audit-website-structure', reason: 'Existing website structure audit requested', when: a => a.audit_existing_website?.includes('Site structure') },
  { template_id: 'std-compile-website-changes', reason: 'Roll audit findings into client deck', when: a => a.audit_existing_website?.length > 0 },
  { template_id: 'std-create-wpengine', reason: 'WP Engine account needs to be created', when: a => a.wp_engine_access === 'We need to create it' },
  { template_id: 'std-create-mood-boards', reason: 'New website build — establish visual direction before aesthetics', when: a => a.website_status === 'New website' },
  { template_id: 'std-build-aesthetic-1', reason: 'Present aesthetic options to client', when: a => a.website_build_aesthetics === 'Yes — 3 options' },
  { template_id: 'std-build-aesthetic-2', reason: 'Present aesthetic options to client', when: a => a.website_build_aesthetics === 'Yes — 3 options' },
  { template_id: 'std-build-aesthetic-3', reason: 'Present aesthetic options to client', when: a => a.website_build_aesthetics === 'Yes — 3 options' },
  { template_id: 'std-build-aesthetic-key-pages', reason: 'Build key pages from approved aesthetic', when: a => a.website_status === 'New website' },
  { template_id: 'std-delegate-remaining-pages', reason: 'Delegate remaining pages to team', when: a => a.website_status === 'New website' },
  { template_id: 'std-logo-concepts', reason: 'Client needs a new logo', when: a => a.needs_logo === 'Yes' },
  { template_id: 'std-brand-kit-presentation', reason: 'Client needs a brand kit', when: a => a.needs_brand_kit === 'Yes' },

  // ── SEO ───────────────────────────────────────────────────
  { template_id: 'std-audit-rankings', reason: 'Baseline existing rankings', when: a => a.services?.includes('seo') },
  { template_id: 'std-verify-gsc-access', reason: 'GSC access needed', when: a => a.services?.includes('seo') && a.has_gsc_access !== 'Yes' },
  { template_id: 'std-baseline-rankings-gsc', reason: 'Lock in baseline rankings in GSC', when: a => a.services?.includes('seo') },
  { template_id: 'std-internal-seo-checklist', reason: 'Run the internal SEO checklist', when: a => a.services?.includes('seo') },
  { template_id: 'std-list-pages-keywords', reason: 'Map pages to target keywords', when: a => a.services?.includes('seo') },
  { template_id: 'std-keyword-research-pages', reason: 'Keyword research for service/specialty pages', when: a => a.services?.includes('seo') },
  { template_id: 'std-assign-yoast-aam', reason: 'Delegate Yoast optimization', when: a => a.services?.includes('seo') },
  { template_id: 'std-brainstorm-blog-topics', reason: 'Generate blog topic list for client approval', when: a => a.services?.includes('seo') && a.wants_blogs === 'Yes' },
  { template_id: 'std-blog-topics-approved', reason: 'Get blog topics approved by client', when: a => a.services?.includes('seo') && a.wants_blogs === 'Yes' },
  { template_id: 'std-assign-first-blogs', reason: 'Hand off first blogs to copywriter', when: a => a.services?.includes('seo') && a.wants_blogs === 'Yes' },

  // ── SOCIAL ────────────────────────────────────────────────
  { template_id: 'checklist-organic-social', reason: 'Organic social work selected', when: a => a.services?.includes('social') },

  // ── ADS ───────────────────────────────────────────────────
  { template_id: 'std-audit-google-ads', reason: 'Existing Google Ads account flagged for audit', when: a => a.has_existing_google_ads === 'Yes — we should audit it' },
  { template_id: 'std-audit-fb-ads', reason: 'Existing FB Ads account flagged for audit', when: a => a.has_existing_fb_ads === 'Yes — we should audit it' },
  { template_id: 'std-audit-existing-lps', reason: 'Existing landing pages need review', when: a => a.services?.includes('ads') && a.needs_lps === 'No — using existing pages' },
  { template_id: 'std-send-google-ads-cid-request', reason: 'CID-based access request', when: a => a.google_ads_access === 'Send CID request' },
  { template_id: 'std-request-google-ads-from-client', reason: 'Request Google Ads access directly from client', when: a => a.google_ads_access === 'Request from client' },
  { template_id: 'std-request-fb-business-access', reason: 'Request FB business account access', when: a => a.fb_business_access === 'Request from client' },
  { template_id: 'checklist-google-ads', reason: 'Google Ads setup', when: a => a.ad_platforms?.includes('google_ads') },
  { template_id: 'checklist-fb-ads', reason: 'Facebook Ads setup', when: a => a.ad_platforms?.includes('fb_ads') },
  { template_id: 'std-keyword-research-ad-groups', reason: 'Keyword research per ad group', when: a => a.ad_platforms?.includes('google_ads') },
  { template_id: 'std-determine-ad-groups', reason: 'Define ad group structure', when: a => a.ad_platforms?.includes('google_ads') },
  { template_id: 'std-build-first-lp', reason: 'Build first landing page', when: a => ['Yes — multiple', 'Yes — just one to start'].includes(a.needs_lps) },
  { template_id: 'std-build-remaining-lps', reason: 'Build additional landing pages', when: a => a.needs_lps === 'Yes — multiple' },
  { template_id: 'std-budget-forecast', reason: 'Prepare budget/forecast (right before launch)', when: a => a.services?.includes('ads') },
  { template_id: 'std-review-budget-with-client', reason: 'Walk client through forecast for sign-off', when: a => a.services?.includes('ads') },

  // ── EMAIL ─────────────────────────────────────────────────
  { template_id: 'std-audit-newsletter-setup', reason: 'Audit existing newsletter setup', when: a => a.services?.includes('email') && a.has_existing_newsletter === 'Yes' },
  { template_id: 'std-confirm-newsletter-platform', reason: 'Confirm/migrate newsletter platform', when: a => a.services?.includes('email') },
  { template_id: 'std-newsletter-header-footer', reason: 'Newsletter header/footer designs', when: a => a.services?.includes('email') },
  { template_id: 'checklist-newsletter', reason: 'Newsletter setup workflow', when: a => a.services?.includes('email') },

  // ── HUBSPOT (cross-cuts ads + email) ──────────────────────
  { template_id: 'checklist-hubspot', reason: 'HubSpot setup for lead tracking/forms/integrations', when: a => a.uses_hubspot === 'Yes' },
]

// ────────────────────────────────────────────────────────────
// HELPERS
// ────────────────────────────────────────────────────────────

export function visibleQuestions(answers) {
  const out = { universal: [], website: [], seo: [], social: [], ads: [], email: [] }
  for (const key of Object.keys(QUESTIONS)) {
    for (const q of QUESTIONS[key]) {
      if (q.showIf && !q.showIf(answers)) continue
      // Service-specific sections only show if that service is selected
      if (key === 'website' && !answers.services?.includes('website')) continue
      if (key === 'seo' && !answers.services?.includes('seo')) continue
      if (key === 'social' && !answers.services?.includes('social')) continue
      if (key === 'ads' && !answers.services?.includes('ads')) continue
      if (key === 'email' && !answers.services?.includes('email')) continue
      out[key].push(q)
    }
  }
  return out
}

export function computeRecommendations(answers) {
  const out = []
  const seen = new Set()
  for (const rule of RECOMMENDATION_RULES) {
    if (seen.has(rule.template_id)) continue
    try {
      if (rule.when(answers)) {
        out.push({ template_id: rule.template_id, reason: rule.reason })
        seen.add(rule.template_id)
      }
    } catch { /* swallow rule errors */ }
  }
  return out
}

// Sort by SORT_ORDER, then alphabetically as fallback
export function sortByExecutionOrder(items, getTemplate) {
  return [...items].sort((a, b) => {
    const ta = getTemplate(a)
    const tb = getTemplate(b)
    const oa = SORT_ORDER[ta?.id] ?? 9999
    const ob = SORT_ORDER[tb?.id] ?? 9999
    if (oa !== ob) return oa - ob
    return (ta?.title || '').localeCompare(tb?.title || '')
  })
}